import { useState, useEffect, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff, Check, AlertTriangle, CheckCircle2, ArrowLeft } from 'lucide-react'
import { api, messaggioErrore } from '../../lib/api'
import { Button } from '../../components/ui/Button'
import styles from './Auth.module.css'

type Fase = 'caricamento' | 'form' | 'successo' | 'token_invalido'

/**
 * Pagina di reset password — aperta cliccando il link nell'email Supabase.
 *
 * Supabase aggiunge `#access_token=…&refresh_token=…&type=recovery` all'URL.
 * Leggiamo il token, mostriamo il form, e POST al backend `/auth/reset-password`
 * che a sua volta chiama l'API Auth Supabase con il Bearer token.
 *
 * Note implementative:
 *  - parsing dell'hash con URLSearchParams (è il pattern standard Supabase)
 *  - se manca `access_token` o non è `type=recovery` → schermata "link invalido"
 *  - requisiti password verificati in tempo reale (almeno 8 chars + Maiuscola + minuscola + numero)
 */
export function ResetPassword() {
  const navigate = useNavigate()
  const [fase, setFase] = useState<Fase>('caricamento')
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [password, setPassword] = useState('')
  const [conferma, setConferma] = useState('')
  const [mostraPassword, setMostraPassword] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  const [caricamento, setCaricamento] = useState(false)

  // ─── Leggi il token dall'hash al mount ─────────────────────────
  useEffect(() => {
    const hash = window.location.hash
    if (!hash || hash.length < 2) {
      setFase('token_invalido')
      return
    }
    const params = new URLSearchParams(hash.slice(1)) // rimuovi il '#'
    const token = params.get('access_token')
    const type = params.get('type')
    const errore = params.get('error') || params.get('error_description')

    if (errore) {
      setFase('token_invalido')
      return
    }
    if (!token || type !== 'recovery') {
      setFase('token_invalido')
      return
    }
    setAccessToken(token)
    setFase('form')
  }, [])

  // ─── Requisiti password in tempo reale ─────────────────────────
  const requisiti = [
    { testo: 'Almeno 8 caratteri',     valido: password.length >= 8 },
    { testo: 'Una lettera maiuscola',  valido: /[A-Z]/.test(password) },
    { testo: 'Una lettera minuscola',  valido: /[a-z]/.test(password) },
    { testo: 'Un numero',              valido: /\d/.test(password) },
  ]
  const tuttiSoddisfatti = requisiti.every((r) => r.valido)
  const passwordMatch = password.length > 0 && password === conferma

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!accessToken) return
    if (!tuttiSoddisfatti) {
      setErrore('La password non soddisfa tutti i requisiti.')
      return
    }
    if (!passwordMatch) {
      setErrore('Le password non corrispondono.')
      return
    }
    setErrore(null)
    setCaricamento(true)
    try {
      await api.post('/auth/reset-password', {
        access_token: accessToken,
        password,
        conferma_password: conferma,
      })
      setFase('successo')
      // Redirect automatico al login dopo 3s
      setTimeout(() => navigate('/accedi?messaggio=password_aggiornata'), 3000)
    } catch (err) {
      setErrore(messaggioErrore(err))
    } finally {
      setCaricamento(false)
    }
  }

  // ─── Render: stato caricamento (token in lettura) ──────────────
  if (fase === 'caricamento') {
    return (
      <div className={styles.wrap}>
        <div className={styles.card} style={{ textAlign: 'center' }}>
          <div className={styles.brand} style={{ justifyContent: 'center' }}>
            <div className={styles.logoBox}>RC</div>
            <span className={styles.brandTesto}>Ring<strong>Coach</strong></span>
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
            Verifica del link in corso…
          </p>
        </div>
      </div>
    )
  }

  // ─── Render: token invalido / scaduto ─────────────────────────
  if (fase === 'token_invalido') {
    return (
      <div className={styles.wrap}>
        <div className={styles.card}>
          <div className={styles.brand}>
            <div className={styles.logoBox}>RC</div>
            <span className={styles.brandTesto}>Ring<strong>Coach</strong></span>
          </div>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: 'var(--warning-soft)', color: 'var(--warning)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <AlertTriangle size={28} />
          </div>
          <h1 className={styles.titolo} style={{ textAlign: 'center' }}>
            Link non valido o scaduto
          </h1>
          <p className={styles.descrizione} style={{ textAlign: 'center' }}>
            Il link di reset è valido solo per <strong>1 ora</strong>.
            Richiedine uno nuovo dalla pagina di recupero password.
          </p>
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Link to="/auth/password-dimenticata">
              <Button taglia="lg" style={{ width: '100%' }}>
                Richiedi nuovo link
              </Button>
            </Link>
            <Link to="/accedi" className={styles.link} style={{ textAlign: 'center' }}>
              <ArrowLeft size={14} style={{ display: 'inline', verticalAlign: -2, marginRight: 4 }} />
              Torna al login
            </Link>
          </div>
        </div>
      </div>
    )
  }

  // ─── Render: successo ──────────────────────────────────────────
  if (fase === 'successo') {
    return (
      <div className={styles.wrap}>
        <div className={styles.card}>
          <div className={styles.brand}>
            <div className={styles.logoBox}>RC</div>
            <span className={styles.brandTesto}>Ring<strong>Coach</strong></span>
          </div>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: 'var(--success-soft)', color: 'var(--success)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <CheckCircle2 size={28} />
          </div>
          <h1 className={styles.titolo} style={{ textAlign: 'center' }}>
            Password aggiornata!
          </h1>
          <p className={styles.descrizione} style={{ textAlign: 'center' }}>
            Accedi con la tua nuova password.
            Verrai reindirizzato automaticamente al login tra qualche secondo.
          </p>
          <div style={{ marginTop: 16 }}>
            <Link to="/accedi">
              <Button taglia="lg" style={{ width: '100%' }}>
                Vai al login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  // ─── Render: form ──────────────────────────────────────────────
  return (
    <div className={styles.wrap}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <div className={styles.logoBox}>RC</div>
          <span className={styles.brandTesto}>Ring<strong>Coach</strong></span>
        </div>

        <h1 className={styles.titolo}>Crea nuova password</h1>
        <p className={styles.descrizione}>
          Scegli una password sicura per il tuo account.
        </p>

        <form onSubmit={submit} className={styles.form}>
          <label className={styles.campo}>
            <span className={styles.lbl}>Nuova password</span>
            <div style={{ position: 'relative' }}>
              <input
                type={mostraPassword ? 'text' : 'password'}
                required
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={styles.input}
                style={{ paddingRight: 40 }}
              />
              <button
                type="button"
                onClick={() => setMostraPassword((v) => !v)}
                style={{
                  position: 'absolute', right: 6, top: 6, width: 32, height: 32,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--text-secondary)', borderRadius: 'var(--radius-md)',
                  background: 'transparent', border: 'none', cursor: 'pointer',
                }}
                aria-label={mostraPassword ? 'Nascondi' : 'Mostra'}
              >
                {mostraPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </label>

          <label className={styles.campo}>
            <span className={styles.lbl}>Conferma nuova password</span>
            <input
              type={mostraPassword ? 'text' : 'password'}
              required
              autoComplete="new-password"
              value={conferma}
              onChange={(e) => setConferma(e.target.value)}
              className={styles.input}
              aria-invalid={conferma.length > 0 && !passwordMatch}
            />
            {conferma.length > 0 && !passwordMatch && (
              <span style={{ fontSize: 12, color: 'var(--danger)', marginTop: 4 }}>
                Le password non corrispondono.
              </span>
            )}
          </label>

          {password.length > 0 && (
            <div style={{
              padding: 12, background: 'var(--bg-subtle)',
              borderRadius: 'var(--radius-md)',
            }}>
              <div style={{
                fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)',
                textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6,
              }}>
                Requisiti password
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {requisiti.map((r) => (
                  <li key={r.testo} style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    fontSize: 13,
                    color: r.valido ? 'var(--success)' : 'var(--text-muted)',
                  }}>
                    {r.valido ? <Check size={12} /> : <span style={{ width: 12, textAlign: 'center' }}>○</span>}
                    {r.testo}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {errore && <div className={styles.errore}>{errore}</div>}

          <Button
            type="submit"
            taglia="lg"
            caricamento={caricamento}
            disabled={!tuttiSoddisfatti || !passwordMatch}
          >
            Aggiorna password
          </Button>
        </form>

        <div className={styles.linkRiga} style={{ marginTop: 20 }}>
          <Link to="/accedi" className={styles.link}>
            <ArrowLeft size={14} style={{ display: 'inline', verticalAlign: -2, marginRight: 4 }} />
            Annulla e torna al login
          </Link>
        </div>
      </div>
    </div>
  )
}
